import React, {useEffect, useState} from 'react'
import {Modal, Badge, Button} from 'react-bootstrap'
import useAppToast from 'src/hooks/useAppToast'
import {useAppDispatch} from 'src/redux/storeHooks'
import {useAppSelector} from 'src/redux/storeHooks'
import {
  approvePaypalSubscriptionAsync,
  logPaypalSubscriptionAsync,
} from 'src/redux/actions/pricingActions'

const PayPalModal: React.FC<{
  selectedPlan: any
  email: string | undefined
  onClose: () => void
}> = ({selectedPlan, email, onClose}) => {
  const {errorToast, successToast} = useAppToast()
  const dispatch = useAppDispatch()
  const {fetchedUserAccountData} = useAppSelector((state) => state.userAccountData)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [paypalRendered, setPaypalRendered] = useState(false)
  const [show, setShow] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [approvedSubscriptionId, setApprovedSubscriptionId] = useState<string | null>(null)

  const approveSubscription = async (subscriptionId: string) => {
    return dispatch(
      approvePaypalSubscriptionAsync({
        subscription_id: subscriptionId,
        plan_id: selectedPlan?.id,
      })
    ).unwrap()
  }

  const logSubscriptionFailure = async (payload: any) => {
    try {
      await dispatch(logPaypalSubscriptionAsync(payload)).unwrap()
    } catch (error) {
      console.error('Failed to log PayPal subscription issue', error)
    }
  }

  useEffect(() => {
    if (!showCancelModal && !paypalRendered) {
      setPaypalRendered(true) // Ensure PayPal buttons render only once

      setTimeout(() => {
        if ((window as any).paypal) {
          ;(window as any).paypal
            .Buttons({
              style: {layout: 'vertical'},
              createSubscription: (data: any, actions: any) => {
                return actions.subscription.create({
                  plan_id: selectedPlan?.description,
                  application_context: {
                    user_action: 'SUBSCRIBE_NOW',
                    brand_name: 'COMMERCIUM',
                  },
                  custom_id: `${Date.now()}_uid_${fetchedUserAccountData?.id ?? 'unknown'}_plan_${
                    selectedPlan?.id ?? selectedPlan?.description ?? 'unknown'
                  }`,
                  subscriber: {
                    email_address: email,
                  },
                })
              },
              onApprove: async (data: any) => {
                try {
                  await approveSubscription(data.subscriptionID)
                  successToast('Payment successful')
                  setApprovedSubscriptionId(data.subscriptionID)
                  setShowSuccessModal(true)
                } catch (error) {
                  await logSubscriptionFailure(data)
                  errorToast('Payment approval failed. Please try again.')
                }
              },
              onCancel: async (data: any) => {
                console.log('paypal payment canceled')
                await logSubscriptionFailure(data)
                setShowCancelModal(true)
                setPaypalRendered(false)
                setShow(true)
              },
              onError: async (err: any) => {
                await logSubscriptionFailure(err)
                errorToast('Payment failed. Please try again.')
              },
            })
            .render('#paypal-buttons')
        } else {
          errorToast('PayPal failed to load. Please try again.')
        }
      }, 100) // Small delay ensures modal is fully loaded before rendering buttons
    }
  }, [showCancelModal, selectedPlan, paypalRendered, email, errorToast, successToast, dispatch])

  const handleClose = () => {
    setShowCancelModal(false)
    setShow(false)
  }

  const handleSuccessClose = () => {
    setShowSuccessModal(false)
    onClose()
    window.location.reload()
  }
  return (
    <>
      {!showCancelModal && !showSuccessModal && (
        <Modal show onHide={onClose} centered>
          <Modal.Header closeButton>
            <Modal.Title className='text-secondary'>Plan Details</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <h5 className='text-lg'>
              Plan : <Badge>{selectedPlan?.plan_name}</Badge>
            </h5>
            <h5 className='text-lg'>
              Price :{' '}
              <Badge bg='success'>
                ${selectedPlan?.price?.value} / {selectedPlan?.billing_frequency}
              </Badge>
            </h5>
            <div id='paypal-buttons' className='d-flex justify-content-center'></div>
          </Modal.Body>
        </Modal>
      )}

      <Modal show={show} onHide={handleClose} centered contentClassName='bg-light-danger'>
        <Modal.Body className='d-flex flex-column align-items-center justify-content-center text-center'>
          <i
            className='fa-regular fa-circle-xmark'
            style={{fontSize: '5rem', color: '#dc3545', marginTop: '1rem'}}
          ></i>
          <h2 className='my-3 text-secondary'>You have cancelled the subscription process.</h2>
          <div className='w-100 d-flex justify-content-end mt-4'>
            <Button variant='danger' onClick={handleClose} className='py-2 px-3 fw-bold'>
              OK
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showSuccessModal} onHide={handleSuccessClose} centered size='lg' backdrop='static'>
        <Modal.Body
          className='d-flex flex-column align-items-center justify-content-center text-center'
          style={{minHeight: '260px'}}
        >
          <div
            className='d-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-25'
            style={{width: '52px', height: '52px'}}
          >
            <i className='fa-solid fa-check text-success' style={{fontSize: '1.5rem'}}></i>
          </div>
          <p
            className='mt-4 mb-4 text-secondary fw-semibold'
            style={{maxWidth: '420px', fontSize: '1.6rem'}}
          >
            You have subscribed to the selected plan. Your new plan is active.
          </p>
          <Button variant='success' onClick={handleSuccessClose} className='py-2 px-4 fw-bold'>
            Ok
          </Button>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default PayPalModal
